import React, { useEffect, useState } from "react";
import {
  Copy,
  Eye,
  Loader2,
  Mail,
  Pencil,
  RotateCcw,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useDeleteHotelEmailTemplate,
  useHotelEmailTemplates,
  usePreviewHotelEmailTemplate,
  useUpsertHotelEmailTemplate,
} from "../hooks/useHotelEmailTemplates";
import { fileService } from "@/services/file-service";
import type {
  HotelEmailTemplate,
  RenderedHotelEmailTemplate,
} from "@/types/hotel-email-template";
import {
  EMAIL_TEMPLATE_TRIGGER_DESCRIPTIONS,
  EMAIL_TEMPLATE_TRIGGER_LABELS,
} from "@/types/hotel-email-template";

type EmailTemplateFormState = {
  title: string;
  subject: string;
  htmlContent: string;
  plainContent: string;
  enabled: boolean;
  heroPhotoId: string;
};

const emptyForm: EmailTemplateFormState = {
  title: "",
  subject: "",
  htmlContent: "",
  plainContent: "",
  enabled: false,
  heroPhotoId: "",
};

type EmailTemplatesCardProps = {
  hotelId: string | null;
};

export function EmailTemplatesCard({ hotelId }: EmailTemplatesCardProps) {
  const { data: templates = [], isLoading } = useHotelEmailTemplates(hotelId);
  const upsertTemplate = useUpsertHotelEmailTemplate(hotelId);
  const deleteTemplate = useDeleteHotelEmailTemplate(hotelId);
  const previewTemplate = usePreviewHotelEmailTemplate(hotelId);

  const [selectedTemplate, setSelectedTemplate] =
    useState<HotelEmailTemplate | null>(null);
  const [form, setForm] = useState<EmailTemplateFormState>(emptyForm);
  const [heroPhotoFile, setHeroPhotoFile] = useState<File | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [preview, setPreview] = useState<RenderedHotelEmailTemplate | null>(
    null,
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (!selectedTemplate) {
      setForm(emptyForm);
      setHeroPhotoFile(null);
      return;
    }

    setForm(templateToForm(selectedTemplate));
    setHeroPhotoFile(null);
  }, [selectedTemplate]);

  const isSaving = upsertTemplate.isPending || isUploadingPhoto;
  const isFormInvalid =
    !form.title.trim() || !form.subject.trim() || !form.htmlContent.trim();

  const updateForm = <K extends keyof EmailTemplateFormState>(
    key: K,
    value: EmailTemplateFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const ensureHeroPhotoId = async (): Promise<string | null> => {
    if (!heroPhotoFile) {
      return form.heroPhotoId.trim() || null;
    }

    setIsUploadingPhoto(true);
    try {
      const fileId =
        await fileService.uploadFileWithPresignedUrl(heroPhotoFile);
      setForm((current) => ({ ...current, heroPhotoId: fileId }));
      setHeroPhotoFile(null);
      return fileId;
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!hotelId || !selectedTemplate || isFormInvalid) return;

    const heroPhotoId = await ensureHeroPhotoId();
    await upsertTemplate.mutateAsync({
      trigger: selectedTemplate.trigger,
      payload: buildPayload(form, heroPhotoId),
    });
    setSelectedTemplate(null);
  };

  const handlePreview = async () => {
    if (!hotelId || !selectedTemplate || isFormInvalid) return;

    const heroPhotoId = await ensureHeroPhotoId();
    const rendered = await previewTemplate.mutateAsync({
      trigger: selectedTemplate.trigger,
      subject: form.subject,
      htmlContent: form.htmlContent,
      plainContent: form.plainContent.trim() || null,
      heroPhotoId,
    });

    setPreview(rendered);
    setIsPreviewOpen(true);
  };

  const handleToggle = (template: HotelEmailTemplate, enabled: boolean) => {
    if (!hotelId) return;

    upsertTemplate.mutate({
      trigger: template.trigger,
      payload: {
        title: template.title,
        subject: template.subject,
        htmlContent: template.htmlContent,
        plainContent: template.plainContent,
        heroPhotoId: template.heroPhotoId,
        enabled,
      },
    });
  };

  const handleReset = (template: HotelEmailTemplate) => {
    if (!template.id) return;

    const confirmed = window.confirm(
      "Reset this email template to the default content?",
    );
    if (!confirmed) return;

    deleteTemplate.mutate(template.id);
  };

  const handleCopyVariable = (variable: string) => {
    const token = `{{${variable}}}`;
    navigator.clipboard
      ?.writeText(token)
      .then(() => toast.success(`${token} copied.`))
      .catch(() => toast.error("Could not copy variable."));
  };

  let cardContent: React.ReactNode;
  if (!hotelId) {
    cardContent = (
      <EmptyState message="Select a hotel to manage email templates." />
    );
  } else if (isLoading) {
    cardContent = <EmptyState message="Loading email templates..." />;
  } else {
    cardContent = (
      <EmailTemplatesTable
        templates={templates}
        isUpdating={upsertTemplate.isPending}
        isDeleting={deleteTemplate.isPending}
        onToggle={handleToggle}
        onEdit={setSelectedTemplate}
        onReset={handleReset}
      />
    );
  }

  return (
    <Card className="max-w-6xl gap-0 py-0 shadow-sm">
      <CardHeader className="mb-6 border-0 px-6 pb-0 pt-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-foreground">
              <Mail className="size-6 text-royal-glow-3" />
              Email templates
            </CardTitle>
            <CardDescription className="mt-2 max-w-2xl">
              Create hotel-specific email templates. SendGrid still delivers the
              email; these templates override SendGrid dynamic templates only
              when enabled.
            </CardDescription>
          </div>
          <Badge variant="new_secondary" className="mt-1">
            SendGrid fallback stays active
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="px-6 pb-6 pt-0">{cardContent}</CardContent>

      <Dialog
        open={Boolean(selectedTemplate)}
        onOpenChange={(open) => {
          if (!open) setSelectedTemplate(null);
        }}
      >
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate
                ? EMAIL_TEMPLATE_TRIGGER_LABELS[selectedTemplate.trigger]
                : "Email template"}
            </DialogTitle>
            <DialogDescription>
              Use variables like {"{{guest_full_name}}"} or {"{{hotel_name}}"}{" "}
              in the subject, HTML, or plain text.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-4">
              <Input
                label="Template title"
                value={form.title}
                onChange={(event) => updateForm("title", event.target.value)}
              />
              <Input
                label="Email subject"
                value={form.subject}
                onChange={(event) => updateForm("subject", event.target.value)}
              />

              <div className="rounded-xl border border-base-border p-4">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Label className="text-sm font-medium text-zinc-900">
                      Hotel photo
                    </Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Optional. If empty, the template uses the hotel carousel
                      photo when available.
                    </p>
                  </div>
                  <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-base-border px-3 text-sm hover:bg-secondary-badge">
                    <Upload className="size-4" />
                    Upload photo
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(event) => {
                        setHeroPhotoFile(event.target.files?.[0] ?? null);
                      }}
                    />
                  </label>
                </div>
                <Input
                  label="Hero photo file ID"
                  value={form.heroPhotoId}
                  placeholder="Optional file ID"
                  onChange={(event) =>
                    updateForm("heroPhotoId", event.target.value)
                  }
                />
                {heroPhotoFile && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Selected: {heroPhotoFile.name}
                  </p>
                )}
              </div>

              <Textarea
                label="HTML content"
                value={form.htmlContent}
                onChange={(event) =>
                  updateForm("htmlContent", event.target.value)
                }
                className="min-h-[320px] font-mono text-xs text-zinc-900"
              />
              <Textarea
                label="Plain text fallback"
                description="Optional. If empty, plain text is generated from the HTML."
                value={form.plainContent}
                onChange={(event) =>
                  updateForm("plainContent", event.target.value)
                }
                className="min-h-[120px] text-zinc-900"
              />
            </div>

            <aside className="space-y-4">
              <div className="rounded-xl border border-base-border bg-muted/20 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm font-medium text-zinc-900">
                      Enable custom template
                    </Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      If disabled, SendGrid dynamic template is used.
                    </p>
                  </div>
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(checked) =>
                      updateForm("enabled", checked)
                    }
                  />
                </div>
              </div>

              <div className="rounded-xl border border-base-border p-4">
                <div className="mb-3 text-sm font-semibold text-zinc-900">
                  Variables
                </div>
                <div className="flex max-h-[420px] flex-wrap gap-2 overflow-y-auto pr-1">
                  {selectedTemplate?.variables.map((variable) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => handleCopyVariable(variable)}
                      className="inline-flex items-center gap-1 rounded-md border border-base-border px-2 py-1 text-xs text-zinc-700 hover:bg-secondary-badge"
                    >
                      <Copy className="size-3" />
                      {`{{${variable}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="new_outline"
              disabled={isFormInvalid || previewTemplate.isPending || isSaving}
              onClick={handlePreview}
            >
              {previewTemplate.isPending || isUploadingPhoto ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Eye className="size-4" />
              )}
              Preview
            </Button>
            <Button
              type="button"
              variant="new_default"
              disabled={isFormInvalid || isSaving}
              onClick={handleSave}
            >
              {isSaving && <Loader2 className="size-4 animate-spin" />}
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
            <DialogDescription>{preview?.subject}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="rounded-xl border border-base-border bg-white p-3">
                <iframe
                  title="Email template preview"
                  sandbox=""
                  srcDoc={preview.html}
                  className="h-[520px] w-full rounded-lg border border-neutral-200 bg-white"
                />
              </div>
              <div className="rounded-xl border border-base-border bg-muted/20 p-4">
                <div className="mb-2 text-sm font-semibold text-zinc-900">
                  Plain text
                </div>
                <pre className="whitespace-pre-wrap text-sm text-zinc-700">
                  {preview.text}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-dashed border-base-border p-8 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

type EmailTemplatesTableProps = {
  templates: HotelEmailTemplate[];
  isUpdating: boolean;
  isDeleting: boolean;
  onToggle: (template: HotelEmailTemplate, enabled: boolean) => void;
  onEdit: (template: HotelEmailTemplate) => void;
  onReset: (template: HotelEmailTemplate) => void;
};

function EmailTemplatesTable({
  templates,
  isUpdating,
  isDeleting,
  onToggle,
  onEdit,
  onReset,
}: EmailTemplatesTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="h-10">
            <TableHead>Template</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="w-[180px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.trigger}>
              <TableCell className="whitespace-normal">
                <div className="font-medium text-zinc-950">
                  {EMAIL_TEMPLATE_TRIGGER_LABELS[template.trigger]}
                </div>
                <div className="mt-1 max-w-sm text-xs text-muted-foreground">
                  {EMAIL_TEMPLATE_TRIGGER_DESCRIPTIONS[template.trigger]}
                </div>
              </TableCell>
              <TableCell className="max-w-[320px] whitespace-normal text-sm text-zinc-700">
                {template.subject}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={template.enabled}
                    onCheckedChange={(checked) => onToggle(template, checked)}
                    disabled={isUpdating}
                  />
                  <span className="text-xs text-muted-foreground">
                    {template.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={template.isCustom ? "new_default" : "outline"}>
                  {template.isCustom ? "Custom" : "Default"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="new_outline"
                    size="icon_sm"
                    onClick={() => onEdit(template)}
                    aria-label={`Edit ${template.title}`}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="new_outline"
                    size="icon_sm"
                    disabled={!template.id || isDeleting}
                    onClick={() => onReset(template)}
                    aria-label={`Reset ${template.title}`}
                  >
                    <RotateCcw className="size-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <p className="mt-4 text-sm text-muted-foreground">
        Disabled templates use the configured SendGrid dynamic template. Enabled
        custom templates are rendered by Guesteria and sent via SendGrid as HTML
        email.
      </p>
    </>
  );
}

function templateToForm(template: HotelEmailTemplate): EmailTemplateFormState {
  return {
    title: template.title,
    subject: template.subject,
    htmlContent: template.htmlContent,
    plainContent: template.plainContent ?? "",
    enabled: template.enabled,
    heroPhotoId: template.heroPhotoId ?? "",
  };
}

function buildPayload(
  form: EmailTemplateFormState,
  heroPhotoId: string | null,
) {
  return {
    title: form.title.trim(),
    subject: form.subject.trim(),
    htmlContent: form.htmlContent,
    plainContent: form.plainContent.trim() || null,
    heroPhotoId,
    enabled: form.enabled,
  };
}
